/**
 * packagingDesign.ts - Moduł projektowania opakowań
 * PlexiSystem Engineering Module  
 * 
 * Rozszerzony o obliczenia kartonów FEFCO z kosztorysem
 */

import {
  PACKAGING,
  calculateBCT,
  calculateCartonDimensions,
  calculatePalletization,
  calculateRequiredECT,
  selectFoamType
} from '../constants/packaging';

export interface PackagingInput {
  product_dims_mm: [number, number, number];
  product_mass_kg: number;
  route: 'local' | 'domestic' | 'intl' | 'air';
  stack_layers?: number;
  fragility: 'low' | 'medium' | 'high';
  quantity?: number;
  fefco_type?: string; // Typ kartonu FEFCO
  custom_price_per_m2?: number; // Własna cena za m²
}

export interface PackagingResult {
  carton: {
    internal_mm: [number, number, number];
    external_mm: [number, number, number];
    BCT_required_N: number;
    ECT_required_kN_m: number;
    material_grade: string;
    fefco_type: string;
    surface_area_m2: number;
    blank_dimensions_mm?: [number, number]; // Wymiary wykroju
    material_usage_m2: number; // Z uwzględnieniem odpadów
  };
  protection: {
    foam_type: string;
    foam_density_kg_m3: number;
    foam_thickness_mm: number;
    corner_protectors: boolean;
    bubble_wrap: boolean;
  };
  palletization: {
    pallet_type: string;
    cartons_per_pallet: number;
    layers: number;
    efficiency_percent: number;
    arrangement: string;
    pallet_weight_kg: number;
    total_weight_kg: number;
  };
  cost_estimate: {
    per_unit: number;
    total: number;
    material_cost: number;
    labor_cost: number;
    breakdown: {
      carton: number;
      foam: number;
      pallet: number;
      corners: number;
      labor: number;
    };
    price_per_m2: number;
  };
  production: {
    cutting_length_m: number;
    creasing_length_m: number;
    production_time_min: number;
    waste_percent: number;
  };
  shipping_marks: string[];
  handling_instructions: string[];
}

// Definicje typów kartonów FEFCO
const FEFCO_TYPES = {
  '0201': { name: 'Klapówka zwykła', waste: 15, complexity: 1.0 },
  '0203': { name: 'Klapówka z zakładkami', waste: 18, complexity: 1.1 },
  '0204': { name: 'Klapówka z pełnymi zakładkami', waste: 20, complexity: 1.2 },
  '0215': { name: 'Klapówka z zamkiem', waste: 22, complexity: 1.3 },
  '0300': { name: 'Pudło teleskopowe', waste: 25, complexity: 1.5 },
  '0301': { name: 'Pudło z wiekiem', waste: 30, complexity: 1.6 },
  '0410': { name: 'Tacka', waste: 12, complexity: 0.8 },
  '0421': { name: 'Tacka narożna', waste: 15, complexity: 0.9 },
  '0427': { name: 'Tacka z pokrywą', waste: 35, complexity: 1.7 },
  '0700': { name: 'Pudło gotowe do klejenia', waste: 10, complexity: 1.4 }
};

/**
 * Główna funkcja projektowania opakowania z kalkulacją FEFCO
 */
export function calculatePackaging(input: PackagingInput): PackagingResult {
  // Wybierz typ FEFCO
  const fefcoType = input.fefco_type || '0201'; // Domyślnie klapówka
  const fefcoSpec = FEFCO_TYPES[fefcoType] || FEFCO_TYPES['0201'];
  
  // Cena za m² (tektura + robocizna)
  const pricePerM2 = input.custom_price_per_m2 || 8.0; // 8 zł/m² domyślnie
  
  // Oblicz wymiary kartonu
  const cartonDims = calculateCartonDimensions(
    input.product_dims_mm,
    input.fragility === 'high'
  );
  
  // Oblicz wymiary wykroju dla FEFCO
  const blankDimensions = calculateFEFCOBlank(
    cartonDims.external_mm,
    fefcoType
  );
  
  // Oblicz powierzchnię materiału
  const surfaceArea = calculateCartonSurfaceArea(cartonDims.external_mm, fefcoType);
  const materialUsage = surfaceArea * (1 + fefcoSpec.waste / 100); // Z odpadami
  
  // Oblicz wymagane BCT
  const stackLayers = input.stack_layers || 5;
  const BCT_required = calculateBCT(
    input.product_mass_kg,
    stackLayers,
    input.route
  );
  
  // Oblicz obwód kartonu
  const perimeter_mm = 2 * (cartonDims.external_mm[0] + cartonDims.external_mm[1]);
  
  // Oblicz wymagane ECT
  const ECT_required = calculateRequiredECT(
    BCT_required,
    perimeter_mm,
    cartonDims.external_mm[2]
  );
  
  // Dobierz klasę tektury na podstawie ECT
  let material_grade = selectCardboardGrade(ECT_required);
  
  // Dobierz pianki
  const foam = selectFoamType(input.product_mass_kg, input.fragility);
  
  // Oblicz paletyzację
  const palletization = calculatePalletization(
    cartonDims.external_mm,
    'euro'
  );
  
  // Dodaj wagę palety i całkowitą
  const palletWeight = 25; // kg dla europalety
  const totalPalletWeight = palletWeight + 
    (input.product_mass_kg + estimateCartonWeight(materialUsage, material_grade)) * 
    palletization.totalCartons;
  
  // Oblicz parametry produkcyjne
  const production = calculateProductionParams(
    cartonDims.external_mm,
    fefcoType
  );
  
  // Oblicz objętość pianki
  const foamVolume_m3 = calculateFoamVolume(
    cartonDims.internal_mm,
    foam.thickness_mm
  );
  
  // KALKULACJA KOSZTÓW
  
  // Koszt kartonu (materiał + robocizna)
  const cartonCost = materialUsage * pricePerM2;
  
  // Podział na materiał i robociznę (założenie: 60% materiał, 40% robocizna)
  const materialCostRatio = 0.6;
  const laborCostRatio = 0.4;
  const materialCost = cartonCost * materialCostRatio;
  const laborCost = cartonCost * laborCostRatio * fefcoSpec.complexity; // Złożoność wpływa na robociznę
  
  // Koszt pianki
  const foamCost = foamVolume_m3 * 200; // ~200 zł/m³ średnio
  
  // Koszt palety (rozłożony na sztuki)
  const palletCostPerUnit = 40 / palletization.totalCartons; // 40 zł za europaletę
  
  // Koszt narożników
  const cornersCost = input.fragility !== 'low' ? 4 * 3 : 0; // 4 narożniki po 3 zł
  
  // Koszt całkowity jednostkowy
  const totalUnitCost = materialCost + laborCost + foamCost + palletCostPerUnit + cornersCost;
  
  // Generuj instrukcje
  const shipping_marks = generateShippingMarks(input.fragility, input.route);
  const handling_instructions = generateHandlingInstructions(input.fragility);
  
  return {
    carton: {
      internal_mm: cartonDims.internal_mm,
      external_mm: cartonDims.external_mm,
      BCT_required_N: BCT_required,
      ECT_required_kN_m: ECT_required,
      material_grade,
      fefco_type: `FEFCO ${fefcoType} - ${fefcoSpec.name}`,
      surface_area_m2: Math.round(surfaceArea * 1000) / 1000,
      blank_dimensions_mm: blankDimensions,
      material_usage_m2: Math.round(materialUsage * 1000) / 1000
    },
    protection: {
      foam_type: foam.type,
      foam_density_kg_m3: foam.density_kg_m3,
      foam_thickness_mm: foam.thickness_mm,
      corner_protectors: input.fragility !== 'low',
      bubble_wrap: input.fragility === 'high'
    },
    palletization: {
      pallet_type: 'EUR (800×1200mm)',
      cartons_per_pallet: palletization.totalCartons,
      layers: palletization.layers,
      efficiency_percent: palletization.efficiency,
      arrangement: palletization.arrangement,
      pallet_weight_kg: palletWeight,
      total_weight_kg: Math.round(totalPalletWeight)
    },
    cost_estimate: {
      per_unit: Math.round(totalUnitCost * 100) / 100,
      total: Math.round(totalUnitCost * (input.quantity || 1) * 100) / 100,
      material_cost: Math.round(materialCost * 100) / 100,
      labor_cost: Math.round(laborCost * 100) / 100,
      breakdown: {
        carton: Math.round((materialCost + laborCost) * 100) / 100,
        foam: Math.round(foamCost * 100) / 100,
        pallet: Math.round(palletCostPerUnit * 100) / 100,
        corners: Math.round(cornersCost * 100) / 100,
        labor: Math.round(laborCost * 100) / 100
      },
      price_per_m2: pricePerM2
    },
    production: production,
    shipping_marks,
    handling_instructions
  };
}

/**
 * Oblicza wymiary wykroju dla różnych typów FEFCO
 */
function calculateFEFCOBlank(
  externalDims: [number, number, number],
  fefcoType: string
): [number, number] {
  const [L, W, H] = externalDims;
  
  let blankLength: number;
  let blankWidth: number;
  
  switch(fefcoType) {
    case '0201': // Klapówka zwykła
      blankLength = 2 * L + 2 * W + 40; // Zakładka klejenia 40mm
      blankWidth = W + H;
      break;
      
    case '0203': // Klapówka z zakładkami
      blankLength = 2 * L + 2 * W + 40;
      blankWidth = W + H + 20; // Dodatkowe zakładki
      break;
      
    case '0204': // Klapówka z pełnymi zakładkami
      blankLength = 2 * L + 2 * W + 40;
      blankWidth = 2 * W + H;
      break;
      
    case '0300': // Pudło teleskopowe (2 części)
      blankLength = 2 * L + 2 * W + 40;
      blankWidth = H + W/2; // Połowa wysokości na część
      break;
      
    case '0410': // Tacka
      blankLength = L + 2 * H;
      blankWidth = W + 2 * H;
      break;
      
    default: // Domyślnie jak 0201
      blankLength = 2 * L + 2 * W + 40;
      blankWidth = W + H;
  }
  
  return [Math.round(blankLength), Math.round(blankWidth)];
}

/**
 * Oblicza rzeczywistą powierzchnię kartonu dla różnych typów FEFCO
 */
function calculateCartonSurfaceArea(
  externalDims: [number, number, number],
  fefcoType: string
): number {
  const [L, W, H] = externalDims.map(d => d / 1000); // mm → m
  
  let area: number;
  
  switch(fefcoType) {
    case '0201': // Klapówka zwykła
    case '0203': // Klapówka z zakładkami
      // Powierzchnia = obwód × wysokość + 2 × górne/dolne klapy
      area = (2 * L + 2 * W) * H + 2 * L * W;
      break;
      
    case '0204': // Klapówka z pełnymi zakładkami
      // Większe zużycie na zakładki
      area = (2 * L + 2 * W) * H + 4 * L * W;
      break;
      
    case '0300': // Pudło teleskopowe
      // Dwie części
      area = 2 * ((2 * L + 2 * W) * (H/2) + L * W);
      break;
      
    case '0301': // Pudło z wiekiem
      // Osobne wieko i dno
      area = (2 * L + 2 * W) * H + 2 * (L * W * 1.1); // 10% więcej na wieko
      break;
      
    case '0410': // Tacka
      // Tylko ściany i dno
      area = L * W + 2 * L * H + 2 * W * H;
      break;
      
    default:
      // Standardowe pudło
      area = 2 * (L * W + L * H + W * H);
  }
  
  return area;
}

/**
 * Dobiera klasę tektury na podstawie wymaganego ECT
 */
function selectCardboardGrade(ECT_kN_m: number): string {
  if (ECT_kN_m <= 2.5) {
    return 'E-fala (1.5mm) 125g/m²';
  } else if (ECT_kN_m <= 4.0) {
    return 'B-fala (3mm) 360g/m²';
  } else if (ECT_kN_m <= 6.0) {
    return 'C-fala (4mm) 440g/m²';
  } else if (ECT_kN_m <= 8.0) {
    return 'BC-fala (6mm) 620g/m²';
  } else if (ECT_kN_m <= 12.0) {
    return 'AAC-fala (15mm) 1100g/m²';
  } else {
    return 'Specjalna 7-warstwowa 1500g/m²';
  }
}

/**
 * Szacuje wagę kartonu
 */
function estimateCartonWeight(area_m2: number, grade: string): number {
  const gradeWeights: Record<string, number> = {
    'E-fala (1.5mm) 125g/m²': 0.125,
    'B-fala (3mm) 360g/m²': 0.360,
    'C-fala (4mm) 440g/m²': 0.440,
    'BC-fala (6mm) 620g/m²': 0.620,
    'AAC-fala (15mm) 1100g/m²': 1.100,
    'Specjalna 7-warstwowa 1500g/m²': 1.500
  };
  
  const weightPerM2 = gradeWeights[grade] || 0.440; // Domyślnie C-fala
  return area_m2 * weightPerM2; // kg
}

/**
 * Oblicza parametry produkcyjne
 */
function calculateProductionParams(
  dims: [number, number, number],
  fefcoType: string
): {
  cutting_length_m: number;
  creasing_length_m: number;
  production_time_min: number;
  waste_percent: number;
} {
  const [L, W, H] = dims.map(d => d / 1000); // mm → m
  const fefcoSpec = FEFCO_TYPES[fefcoType] || FEFCO_TYPES['0201'];
  
  let cuttingLength: number;
  let creasingLength: number;
  
  switch(fefcoType) {
    case '0201': // Klapówka zwykła
      cuttingLength = 2 * (L + W + H);
      creasingLength = 4 * (L + W) + 8 * H;
      break;
      
    case '0410': // Tacka
      cuttingLength = 2 * (L + W);
      creasingLength = 2 * (L + W) + 4 * H;
      break;
      
    default:
      cuttingLength = 2 * (L + W + H);
      creasingLength = 4 * (L + W) + 8 * H;
  }
  
  // Czas produkcji (wykrawanie + bigowanie + składanie)
  const cuttingTime = cuttingLength * 0.5; // 0.5 min/m cięcia
  const creasingTime = creasingLength * 0.3; // 0.3 min/m bigowania
  const foldingTime = fefcoSpec.complexity * 2; // 2 min bazowo × złożoność
  
  return {
    cutting_length_m: Math.round(cuttingLength * 100) / 100,
    creasing_length_m: Math.round(creasingLength * 100) / 100,
    production_time_min: Math.round((cuttingTime + creasingTime + foldingTime) * 10) / 10,
    waste_percent: fefcoSpec.waste
  };
}

/**
 * Oblicza objętość pianki ochronnej
 */
function calculateFoamVolume(
  internalDims: [number, number, number],
  thickness_mm: number
): number {
  const t = thickness_mm / 1000; // mm → m
  const [l, w, h] = internalDims.map(d => d / 1000);
  
  // Pianka na wszystkich ściankach
  const volume = 2 * (l * w * t) + // Góra i dół
                 2 * (l * h * t) + // Przód i tył
                 2 * (w * h * t);  // Boki
  
  return volume;
}

/**
 * Generuje oznaczenia transportowe według norm
 */
function generateShippingMarks(
  fragility: 'low' | 'medium' | 'high',
  route: string
): string[] {
  const marks: string[] = [];
  
  // Podstawowe oznaczenia ISO 780
  marks.push('↑ THIS WAY UP / DO GÓRY');
  marks.push('☂ KEEP DRY / CHRONIĆ PRZED WILGOCIĄ');
  
  if (fragility === 'high') {
    marks.push('🍷 FRAGILE / OSTROŻNIE SZKŁO');
    marks.push('⚠ DO NOT DROP / NIE UPUSZCZAĆ');
    marks.push('🚫 DO NOT STACK / NIE PIĘTROWAĆ');
    marks.push('✋ HANDLE WITH CARE / OSTROŻNIE');
  } else if (fragility === 'medium') {
    marks.push('✋ HANDLE WITH CARE / OSTROŻNIE');
    marks.push(`📦 MAX ${5} LAYERS / MAX ${5} WARSTW`);
  } else {
    marks.push(`📦 MAX ${8} LAYERS / MAX ${8} WARSTW`);
  }
  
  if (route === 'intl' || route === 'air') {
    marks.push('⊕ CENTER OF GRAVITY / ŚRODEK CIĘŻKOŚCI');
    marks.push('🌡 TEMP: +5°C to +35°C');
    marks.push('💧 HUMIDITY < 70%');
  }
  
  // Kody kreskowe i numery
  marks.push('GTIN: [_____________]');
  marks.push('SSCC: [_____________]');
  marks.push('LOT/BATCH: [_______]');
  
  return marks;
}

/**
 * Generuje szczegółowe instrukcje obsługi
 */
function generateHandlingInstructions(
  fragility: 'low' | 'medium' | 'high'
): string[] {
  const instructions: string[] = [];
  
  // Ogólne
  instructions.push('1. ROZŁADUNEK:');
  instructions.push('   • Używaj wózka widłowego lub paleciaka');
  instructions.push('   • Wkładaj widły na całą głębokość palety');
  instructions.push('   • Nie przewracaj i nie kantuj opakowania');
  
  if (fragility === 'high') {
    instructions.push('   • Rozładowuj pojedynczo, nie stosami');
    instructions.push('   • Używaj pasów zabezpieczających');
    instructions.push('   • Minimalna 2-osobowa obsługa');
  }
  
  instructions.push('');
  instructions.push('2. SKŁADOWANIE:');
  instructions.push('   • Temperatura: 5-35°C');
  instructions.push('   • Wilgotność: <70%');
  instructions.push('   • Przechowuj w suchym, przewiewnym miejscu');
  instructions.push('   • Chroń przed bezpośrednim nasłonecznieniem');
  
  if (fragility !== 'low') {
    instructions.push('   • Nie składuj bezpośrednio na podłodze');
    instructions.push('   • Zachowaj min. 10cm odstęp od ścian');
  }
  
  instructions.push('');
  instructions.push('3. TRANSPORT:');
  instructions.push('   • Zabezpiecz ładunek pasami lub folią');
  instructions.push('   • Rozmieść równomiernie ciężar');
  instructions.push('   • Unikaj gwałtownego hamowania');
  
  if (fragility === 'high') {
    instructions.push('   • Transport w pozycji pionowej');
    instructions.push('   • Oddziel od ciężkich ładunków');
    instructions.push('   • Stosuj maty antypoślizgowe');
  }
  
  return instructions;
}

/**
 * Eksportuje listę dostępnych typów FEFCO
 */
export function getAvailableFEFCOTypes(): Array<{code: string, name: string, waste: number}> {
  return Object.entries(FEFCO_TYPES).map(([code, spec]) => ({
    code,
    name: spec.name,
    waste: spec.waste
  }));
}
